/**
 * Fase 2A · P0-3 · ClienteOnboarding
 * Blueprint §7.3.5 · 4 pasos skip-friendly: nombre · vehículos · preferencias · foto.
 * Persistencia localStorage. Fase 2B integrará server-side vía customer_area.
 */
import { useNavigate } from "react-router-dom";
import { useCustomerAuth, customerApi } from "../../lib/customerAuth";
import OnboardingWizard from "../../components/onboarding/OnboardingWizard";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  UserCircle, CarSimple, Heart, ImageSquare, MapPin, Plus, X as XIcon,
} from "@phosphor-icons/react";

const SECTORS = [
  { key: "detailing", label: "Detailing" },
  { key: "mecanica", label: "Mecánica" },
  { key: "chapa", label: "Chapa & Pintura" },
  { key: "neumaticos", label: "Neumáticos" },
  { key: "lavado", label: "Lavado" },
  { key: "cristaleria", label: "Cristalería" },
];

export default function ClienteOnboarding() {
  const navigate = useNavigate();
  const { account, refresh } = useCustomerAuth();

  // Fase 2C · Server-side persistence · guarda cuando el usuario está logueado
  const persistToServer = async (data) => {
    if (!account) return; // Anonymous users: only localStorage
    const payload = {};
    if (data.name && data.name !== account.name) payload.name = data.name;
    if (Array.isArray(data.license_plates)) {
      payload.license_plates = data.license_plates.filter(Boolean);
    }
    if (data.city) payload.default_address = data.city;
    if (Object.keys(payload).length === 0) return;
    try {
      await customerApi.put("/customer-auth/me", payload);
      await refresh?.();
    } catch { /* silent · localStorage still persists */ }
  };

  const steps = [
    {
      id: "hello",
      label: "Bienvenido",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <UserCircle weight="fill" /> Cliente
          </div>
          <h2 className="font-display text-2xl font-black tracking-tighter">¿Cómo te llamamos?</h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Personaliza tu experiencia. Podrás cambiar todo esto más tarde en Mi cuenta.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre</Label>
            <Input
              value={data.name || ""}
              onChange={(e) => updateData({ name: e.target.value })}
              placeholder="Ej. María"
              className="mt-1"
              data-testid="cliente-onb-name"
            />
          </div>
        </div>
      ),
    },
    {
      id: "vehicles",
      label: "Tus vehículos",
      render: ({ data, updateData }) => {
        const plates = Array.isArray(data.license_plates) ? data.license_plates : [];
        const addPlate = () => {
          const v = (data._plate_input || "").trim().toUpperCase();
          if (!v) return;
          const next = [...plates.filter((p) => p && p !== v), v];
          updateData({ license_plates: next.slice(0, 5), _plate_input: "" });
        };
        const remove = (p) => updateData({ license_plates: plates.filter((x) => x !== p) });
        return (
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
              <CarSimple weight="fill" /> Matrículas (opcional)
            </div>
            <h2 className="font-display text-xl font-bold">Tus coches</h2>
            <p className="text-sm text-[var(--ag-text-dim)]">
              Guarda las matrículas para reservar en 30s la próxima vez.
            </p>
            <div className="flex gap-2">
              <Input
                value={data._plate_input || ""}
                onChange={(e) => updateData({ _plate_input: e.target.value })}
                placeholder="1234ABC"
                maxLength={10}
                className="font-mono-tab flex-1"
                data-testid="cliente-onb-plate-input"
              />
              <button
                type="button"
                onClick={addPlate}
                className="inline-flex items-center gap-1 px-3 py-2 border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] rounded text-xs font-medium hover:bg-[var(--ag-gold)]/10"
                data-testid="cliente-onb-plate-add"
              >
                <Plus size={12} weight="bold" /> Añadir
              </button>
            </div>
            {plates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {plates.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded font-mono-tab text-xs font-bold"
                    data-testid={`cliente-onb-plate-chip-${p}`}
                  >
                    <CarSimple size={11} weight="duotone" className="text-[var(--ag-gold)]" />
                    {p}
                    <button type="button" onClick={() => remove(p)} className="text-[var(--ag-text-dim)] hover:text-red-400 ml-1">
                      <XIcon size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "preferences",
      label: "Preferencias",
      render: ({ data, updateData }) => {
        const favs = data.favorite_sectors || [];
        const toggle = (k) => updateData({
          favorite_sectors: favs.includes(k) ? favs.filter((x) => x !== k) : [...favs, k],
        });
        return (
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
              <Heart weight="fill" /> Lo que más te interesa
            </div>
            <h2 className="font-display text-xl font-bold">Sectores favoritos (opcional)</h2>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Ciudad preferida</Label>
              <div className="mt-1 relative">
                <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
                <Input
                  value={data.city || ""}
                  onChange={(e) => updateData({ city: e.target.value })}
                  placeholder="Ej. Barcelona"
                  className="pl-9"
                  data-testid="cliente-onb-city"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Sectores</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SECTORS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggle(s.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      favs.includes(s.key)
                        ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]"
                        : "bg-transparent text-[var(--ag-text-dim)] border-[var(--ag-border)] hover:border-[var(--ag-gold)]/40"
                    }`}
                    data-testid={`cliente-onb-sector-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "avatar",
      label: "Foto perfil",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <ImageSquare weight="fill" /> Personaliza
          </div>
          <h2 className="font-display text-xl font-bold">Foto de perfil (opcional)</h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Sube tu foto o pega un enlace. Solo se muestra en Mi cuenta.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">URL de tu foto</Label>
            <Input
              type="url"
              value={data.avatar_url || ""}
              onChange={(e) => updateData({ avatar_url: e.target.value })}
              placeholder="https://…"
              className="mt-1"
              data-testid="cliente-onb-avatar"
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <OnboardingWizard
      roleKey="cliente"
      eyebrow="Onboarding · Cliente"
      title="Personaliza tu experiencia"
      subtitle="4 pasos opcionales. Puedes saltar cualquiera."
      steps={steps}
      initialData={{
        name: account?.name || "",
        license_plates: account?.license_plates || [],
        city: account?.default_address || "",
      }}
      homeRoute="/mi-cuenta"
      homeCta="Ir a Mi cuenta"
      onComplete={async (data) => {
        await persistToServer(data);
        navigate("/mi-cuenta");
      }}
      onSkip={() => navigate("/mi-cuenta")}
    />
  );
}
